import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Chip,
  Divider,
  Link,
  Card,
  CardContent,
  Paper,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
} from '@mui/material';
import rootAxios from 'requests/rootAxios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import LoopIcon from '@mui/icons-material/Loop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import trainingTypes from 'Constants/trainingTypes';
dayjs.extend(customParseFormat);

const getStatusIcon = (row) => {
  const currentDate = dayjs();
  const parsedStartDate = row.dates?.[0]?.date ? dayjs(row.dates[0].date) : null;
  const parsedEndDate = row.datesEnd?.[0]?.date ? dayjs(row.datesEnd[0].date) : null;
  const isApproved = row.isApproved ?? false;
  const isStartingSoon = parsedStartDate?.isAfter(currentDate);
  const isInProgress =
    !isStartingSoon &&
    parsedStartDate &&
    (parsedStartDate.isSame(currentDate) || parsedStartDate.isBefore(currentDate)) &&
    parsedEndDate &&
    (parsedEndDate.isSame(currentDate) || parsedEndDate.isAfter(currentDate));
  const isFinished = parsedEndDate?.isBefore(currentDate);

  if (!isApproved) {
    return {
      icon: <WorkHistoryIcon color="error" />,
      text: 'Approval Pending',
      color: 'error',
    };
  }
  if (isStartingSoon) {
    return {
      icon: <AccessAlarmIcon color="error" />,
      text: 'Starting Soon',
      color: 'error',
    };
  }
  if (isInProgress) {
    const Icon = row.attendencePending ? WarningAmberIcon : LoopIcon;
    const color = row.attendencePending ? 'warning' : 'action';
    const text = row.attendencePending ? 'Attendance pending' : 'In Progress';
    return {
      icon: <Icon color={color} />,
      text,
      color,
    };
  }
  if (isFinished) {
    const Icon = row.attendencePending ? WarningAmberIcon : CheckCircleIcon;
    const color = row.attendencePending ? 'warning' : 'success';
    const text = row.attendencePending ? 'Attendance pending' : 'Finished';
    return {
      icon: <Icon color={color} />,
      text,
      color,
    };
  }
  return null;
};

const ViewBatchModal = ({ open, onClose, batchId }) => {
  const [batchDetails, setBatchDetails] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extraBatchDetails, setExtraBatchDetails] = useState(null);

  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (!batchId) return;

      try {
        setLoading(true);
        const response = await rootAxios.post('/user/get-detailed-batch', { batchId });
        if (response?.data?.data?.batch) {
          console.log('response.data.data.batch.emailConfig', response.data.data.batch.emailConfig);

          setBatchDetails(response.data.data.batch);
          setEmployeeDetails(response.data.data.batchTraineesDataWithNomination);
          setExtraBatchDetails(response.data.data.extraData);
        }
      } catch (err) {
        setError('Failed to fetch batch details');
        console.error('Error fetching batch details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open && batchId) {
      fetchBatchDetails();
    }
  }, [batchId, open]);

  const statusInfo = batchDetails ? getStatusIcon(batchDetails) : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  const renderApprovalTimeline = (tasksApprovedBy, pendingAt) => {
    if (!tasksApprovedBy?.length && !pendingAt?.length) return null;

    const getStepName = (step) => {
      const isThreeStepFlow = [
        trainingTypes.REGULAR_INDUCTION,
        trainingTypes.INDIVIDUAL_LENDING,
        trainingTypes.PRAGATI_TRAINING,
      ].includes(batchDetails.trainingType);

      if (isThreeStepFlow) {
        switch (step) {
          case 1:
            return 'RM Approval';
          case 2:
            return 'CH Approval';
          case 3:
            return 'Invitation Mail';
          default:
            return `Step ${step}`;
        }
      } else {
        switch (step) {
          case 1:
            return 'CH Approval';
          case 2:
            return 'Invitation Mail';
          default:
            return `Step ${step}`;
        }
      }
    };

    const allTasks = [...(tasksApprovedBy || [])];
    allTasks.sort((a, b) => a.step - b.step);

    return (
      <Card elevation={2} sx={{ marginBottom: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <AssignmentIcon sx={{ marginRight: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              Approval Timeline
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <Box
              sx={{
                position: 'absolute',
                left: 20,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: 'grey.300',
                zIndex: 1,
              }}
            />

            {allTasks.map((task, index) => (
              <Box key={index} sx={{ position: 'relative', marginBottom: 3 }}>
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: 8,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: task.completed
                      ? task.isAutoApproved
                        ? 'info.main'
                        : 'success.main'
                      : 'warning.main',
                    border: '3px solid white',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {task.completed ? (
                    task.isAutoApproved ? (
                      <AutoAwesomeIcon sx={{ fontSize: 10, color: 'white' }} />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: 10, color: 'white' }} />
                    )
                  ) : (
                    <PendingActionsIcon sx={{ fontSize: 10, color: 'white' }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ marginLeft: 6 }}>
                  <Card elevation={1} sx={{ padding: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                          {getStepName(task.step)}
                        </Typography>
                        {task.completed && task.isAutoApproved && (
                          <Chip
                            icon={<AutoAwesomeIcon />}
                            label="Auto Approved"
                            color="info"
                            size="small"
                            variant="filled"
                          />
                        )}
                      </Box>
                      <Chip
                        label={task.completed ? 'Completed' : 'Pending'}
                        color={task.completed ? 'success' : 'warning'}
                        size="small"
                        variant={task.completed && task.isAutoApproved ? 'outlined' : 'filled'}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                      <PersonIcon sx={{ marginRight: 1, fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {task.assignedTo?.employeeName || task.employeeName}(
                        {task.assignedTo?.employeeId || task.employeeId})
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                      Role: {task.assignedTo?.roleName || task.roleName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                      isAutoApproved: {task.isAutoApproved ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                      {dayjs(task.lastTaskUpdated || task.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>

                    {task.comments && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                        Comments: {task.comments}
                      </Typography>
                    )}
                  </Card>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderEmailConfig = (config) => {
    console.log('config', config);
    if (!config?.steps?.length) return null;

    const getStepName = (index) => {
      const isThreeStepFlow = [
        trainingTypes.REGULAR_INDUCTION,
        trainingTypes.INDIVIDUAL_LENDING,
        trainingTypes.PRAGATI_TRAINING,
      ].includes(batchDetails.trainingType);

      if (isThreeStepFlow) {
        switch (index) {
          case 0:
            return 'For RM approval';
          case 1:
            return 'For CH approval';
          case 2:
            return 'Invitation mail';
          default:
            return `Step ${index + 1}`;
        }
      } else {
        switch (index) {
          case 0:
            return 'For CH approval';
          case 1:
            return 'Invitation mail';
          default:
            return `Step ${index + 1}`;
        }
      }
    };

    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <EmailIcon sx={{ marginRight: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              Email Configuration
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Steps: {config.totalSteps}
          </Typography>
          {config.steps.map((step, index) => (
            <Card key={index} elevation={1} sx={{ marginBottom: 2, padding: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', marginBottom: 1 }}>
                {getStepName(index)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    To:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {step.to.map((email, emailIndex) => (
                      <Chip key={emailIndex} label={email} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    CC:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {step.cc.map((email, emailIndex) => (
                      <Chip key={emailIndex} label={email} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Card>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderDates = (dates, datesEnd) => {
    if (!dates?.length) return null;

    return (
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <ScheduleIcon sx={{ marginRight: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              Training Schedule
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {dates.map((date, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card elevation={1} sx={{ padding: 2, backgroundColor: 'grey.50' }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: '600', color: 'primary.main', marginBottom: 1 }}
                  >
                    {date.day}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: '500', marginBottom: 0.5 }}>
                    {formatDate(date.date)}
                  </Typography>
                  {datesEnd?.[index]?.date && (
                    <Typography variant="body2" color="text.secondary">
                      End Time: {datesEnd[index].date}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: '600',
          fontSize: '1.5rem',
        }}
      >
        Batch Details - {batchDetails?.batchId}
      </DialogTitle>
      <DialogContent sx={{ padding: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography color="error" variant="h6">
              {error}
            </Typography>
          </Box>
        ) : batchDetails ? (
          <Box>
            {/* Status and Basic Info */}
            <Card elevation={3} sx={{ marginBottom: 3, borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {statusInfo?.icon}
                    <Typography variant="h6" sx={{ marginLeft: 1, fontWeight: '600' }}>
                      {statusInfo?.text || 'Unknown Status'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={batchDetails.trainingType?.replace(/_/g, ' ')}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip label={batchDetails.batchType} color="secondary" variant="outlined" />
                    <Chip label={batchDetails.duration} color="info" variant="outlined" />
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                      <PersonIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Trainer
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: '600' }}>
                          {extraBatchDetails?.trainerName} ({extraBatchDetails?.trainerId})
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                      <GroupIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Trainees Count
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: '600' }}>
                          {employeeDetails?.length || 0} Trainees
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Approval Timeline */}
            {renderApprovalTimeline(
              extraBatchDetails?.tasksApprovedBy,
              extraBatchDetails?.pendingAt
            )}

            {/* Training Schedule */}
            {renderDates(batchDetails.dates, batchDetails.datesEnd)}

            {/* Location and Contact Info */}
            <Card elevation={2} sx={{ marginBottom: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                  <LocationOnIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: '600' }}>
                    Location & Contact
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Training Location
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: '500' }}>
                      {batchDetails.trainingRoLocation || 'Not specified'}
                    </Typography>
                  </Grid>
                  {batchDetails.address && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1">{batchDetails.address}</Typography>
                    </Grid>
                  )}
                  {batchDetails.googleMapsLink && (
                    <Grid item xs={12}>
                      <Link
                        href={batchDetails.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <LinkIcon sx={{ marginRight: 0.5 }} />
                        View on Google Maps
                      </Link>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Regions and Branches */}
            <Grid container spacing={3} sx={{ marginBottom: 3 }}>
              {batchDetails.region?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: 2 }}>
                        Regions ({batchDetails.region.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {batchDetails.region.map((region, index) => (
                          <Chip key={index} label={region.label} variant="outlined" />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {batchDetails.branch?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: 2 }}>
                        Branches ({batchDetails.branch.length})
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          maxHeight: 200,
                          overflow: 'auto',
                        }}
                      >
                        {batchDetails.branch.map((branch, index) => (
                          <Chip key={index} label={branch.label} variant="outlined" size="small" />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Trainees List */}
            <Card elevation={2} sx={{ marginBottom: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                  <GroupIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: '600' }}>
                    Batch Trainees ({employeeDetails?.length || 0})
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {employeeDetails?.map((trainee, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card elevation={1} sx={{ padding: 1.5, backgroundColor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              marginRight: 1,
                              backgroundColor: 'primary.main',
                              width: 32,
                              height: 32,
                            }}
                          >
                            {trainee.employeeName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: '600' }}>
                              {trainee.employeeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {trainee.employeeId}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Email Configuration */}
            {renderEmailConfig(batchDetails.emailConfig)}

            {/* Description */}
            {batchDetails.description && (
              <Card elevation={2} sx={{ marginTop: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: 2 }}>
                    Description
                  </Typography>
                  <Typography variant="body1">{batchDetails.description}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography variant="h6" color="text.secondary">
              No batch details found
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewBatchModal;
